import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Compass, MapPinned, ShieldCheck, Sparkles, Users, ListChecks,
  Building2, Home as HomeIcon, Landmark, Rocket, KeyRound, TrendingUp,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useSEO, orgJsonLd } from "@/lib/seo";
import ProjectCard from "@/components/common/ProjectCard";
import LocationCard from "@/components/common/LocationCard";
import HeroCarousel from "@/components/common/HeroCarousel";
import { RequirementTrigger } from "@/components/common/RequirementFlow";
import NewsWire from "@/components/common/NewsWire";
import { GridSkeleton, SectionLabel } from "@/components/common/states";

const HERO_IMG = "https://images.unsplash.com/photo-1705955463252-e3f670e4041b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const WHY = [
  { icon: Compass, title: "Curated Property Options", desc: "A hand-picked selection of residential and commercial properties across Pune, filtered to match what you actually need." },
  { icon: MapPinned, title: "Location Expertise", desc: "Deep, area-level knowledge of Pune's micro-markets — from Wakad and Baner to Kharadi and Koregaon Park." },
  { icon: ListChecks, title: "Requirement-Based Matching", desc: "Share your budget, configuration and purpose, and we shortlist options that genuinely fit." },
  { icon: ShieldCheck, title: "Transparent Information", desc: "Clear property details with no inflated claims. RERA and pricing details are shared on enquiry." },
  { icon: Users, title: "Personalized Consultation", desc: "Speak with an advisor who understands your priorities and guides you end to end." },
  { icon: Sparkles, title: "Assisted Discovery", desc: "From first search to site visit, a structured process that respects your time." },
];

const CATEGORIES = [
  { icon: HomeIcon, label: "West Pune", to: "/projects?region=WEST" },
  { icon: Building2, label: "East Pune", to: "/projects?region=EAST" },
  { icon: Landmark, label: "North Pune", to: "/projects?region=NORTH" },
  { icon: Rocket, label: "New Launch", to: "/projects?status=new-launch" },
  { icon: KeyRound, label: "Ready to Move", to: "/projects?status=ready-to-move" },
  { icon: TrendingUp, label: "Prime / Central", to: "/projects?region=CENTRAL" },
];

const STEPS = [
  { n: "01", title: "Tell us what you need", desc: "Share your budget, preferred locations and configuration." },
  { n: "02", title: "Explore suitable properties", desc: "Browse a shortlist matched to your requirement." },
  { n: "03", title: "Compare your options", desc: "Weigh price, location, amenities and status side by side." },
  { n: "04", title: "Speak with an advisor", desc: "Get answers on pricing, RERA and possession." },
  { n: "05", title: "Schedule a visit", desc: "See your shortlisted properties in person." },
];

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [locations, setLocations] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);

  useSEO({
    title: "Property in Pune | New & Upcoming Residential Projects",
    description: "Explore new projects, upcoming residential projects, premium apartments and property investment opportunities across Pune. Tell Endeavour Castle what and where you're looking for and we'll identify relevant early-bird opportunities.",
    path: "/",
    image: HERO_IMG,
    jsonLd: orgJsonLd,
  });

  useEffect(() => {
    apiGet("/projects", { featured: true }).then((d) => setFeatured(d.items)).catch(() => setFeatured([]));
    apiGet("/locations", { featured: true }).then(setLocations).catch(() => setLocations([]));
    // Hero rotation uses ONLY verified project imagery from the database.
    apiGet("/projects", { limit: 100 }).then((d) => {
      const slides = (d.items || [])
        .filter((p) => p.primary_image)
        .slice(0, 6)
        .map((p) => ({ image: p.primary_image, projectName: p.name, location: p.location_name, alt: `${p.name}, ${p.location_name}` }));
      setHeroSlides(slides);
    }).catch(() => setHeroSlides([]));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[560px] sm:min-h-[620px] flex items-center">
        <HeroCarousel slides={heroSlides} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40" aria-hidden />
        <div className="relative ec-container py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-accent mb-4">Pune Real Estate Advisory</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-white">
              Want to find out about upcoming early-bird opportunities in your favourite location?
            </h1>
            <p className="mt-5 text-lg text-white/85 max-w-xl leading-relaxed">
              Tell us the location, property type, budget or anything else you're looking for. We'll help you identify relevant upcoming opportunities in Pune — new launches, upcoming residential projects and premium apartments.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <RequirementTrigger ctaSource="hero" testId="hero-requirement"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-accent text-accent-foreground text-sm font-semibold hover:bg-accent/90 transition-colors">
                Tell Us What You're Looking For <ArrowRight className="h-4 w-4" aria-hidden />
              </RequirementTrigger>
              <Link to="/projects" data-testid="hero-explore"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white/10 text-white border border-white/40 text-sm font-semibold backdrop-blur hover:bg-white/20 transition-colors">
                Explore Projects
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/70">New projects across <span className="text-white/90 font-medium">Wakad · Baner · Hinjewadi · Kharadi</span> and every Pune micro-market</p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="ec-container py-20 sm:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <SectionLabel>Featured</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl text-foreground">Featured Projects in Pune</h2>
          </div>
          <Link to="/projects" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:gap-2 transition-all">
            View all <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
        {featured === null ? (
          <GridSkeleton />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
          </div>
        )}
      </section>

      {/* LOCATIONS */}
      <section className="bg-muted py-20 sm:py-24">
        <div className="ec-container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <SectionLabel>Neighbourhoods</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl text-foreground">Explore Pune by Location</h2>
            </div>
            <Link to="/locations" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:gap-2 transition-all">
              All locations <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          {locations === null ? (
            <GridSkeleton count={4} />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {(locations.length ? locations : []).slice(0, 8).map((l, i) => <LocationCard key={l.slug} location={l} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* WHY */}
      <section className="ec-container py-20 sm:py-24">
        <div className="max-w-2xl mb-12">
          <SectionLabel>Why Endeavour Castle</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl text-foreground">A more considered way to find property in Pune</h2>
          <p className="mt-4 text-muted-foreground">We focus on matching genuine requirements with the right options — no pressure, no inflated promises.</p>
        </div>
        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3 rounded-md overflow-hidden border border-border">
          {WHY.map((w) => (
            <div key={w.title} className="bg-white p-8">
              <div className="h-11 w-11 rounded-sm bg-primary/5 text-primary grid place-items-center mb-5">
                <w.icon className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="font-display text-xl text-foreground mb-2">{w.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-primary py-20 sm:py-24">
        <div className="ec-container">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">Browse by Category</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white">Property Categories</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c) => (
              <Link key={c.label} to={c.to} data-testid={`category-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="group flex flex-col items-center gap-3 p-6 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 hover:border-accent/50 transition-colors text-center">
                <c.icon className="h-7 w-7 text-accent" aria-hidden />
                <span className="text-sm font-medium text-white">{c.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="ec-container py-20 sm:py-24">
        <div className="max-w-2xl mb-12">
          <SectionLabel>How It Works</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl text-foreground">From requirement to keys, in five steps</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {STEPS.map((s) => (
            <div key={s.n} className="relative">
              <span className="font-display text-5xl text-accent/30">{s.n}</span>
              <h3 className="font-display text-lg text-foreground mt-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REQUIREMENT CTA */}
      <section className="bg-primary py-16 sm:py-20" id="requirement">
        <div className="ec-container text-center max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">Requirement-First</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white leading-tight">Tell us what you're looking for</h2>
          <p className="mt-4 text-white/80">Share your requirement in your own words — location, budget, configuration or anything else. We'll identify relevant upcoming opportunities in Pune.</p>
          <div className="mt-7 flex justify-center">
            <RequirementTrigger ctaSource="home_band"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-accent text-accent-foreground text-sm font-semibold hover:bg-accent/90 transition-colors">
              Tell Us What You're Looking For <ArrowRight className="h-4 w-4" aria-hidden />
            </RequirementTrigger>
          </div>
        </div>
      </section>

      {/* NEWS WIRE */}
      <NewsWire />

      {/* FINAL CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1714983007778-b370188238b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Modern residential towers in Pune" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-primary/85" />
        </div>
        <div className="relative ec-container text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-5xl text-white leading-tight">Let's Find Your Next Property in Pune</h2>
          <p className="mt-4 text-white/80 text-lg">Explore curated listings or talk to an advisor who knows Pune's neighbourhoods inside out.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/projects" className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-accent text-accent-foreground text-sm font-semibold hover:bg-accent/90 transition-colors">
              Explore Properties
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-white/10 border border-white/40 text-white text-sm font-semibold hover:bg-white/20 transition-colors">
              Talk to an Advisor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
